import { enUS, nl as nlLocale } from "date-fns/locale"
import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { initReactI18next } from "react-i18next"
import { en, nl } from "./locales"

export const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "nl", label: "Nederlands", short: "NL" },
] as const

export type Language = (typeof LANGUAGES)[number]["code"]

export const LANGUAGE_STORAGE_KEY = "mit.lang"

export const resources = {
  en: { translation: en },
  nl: { translation: nl },
} as const

export function isLanguage(v: unknown): v is Language {
  return LANGUAGES.some((l) => l.code === v)
}

/** Current language, normalised to one of {@link LANGUAGES}. */
export function currentLanguage(): Language {
  const lng = (i18n.resolvedLanguage ?? i18n.language ?? "en").slice(0, 2)
  return isLanguage(lng) ? lng : "en"
}

/** date-fns locale matching the active UI language. */
export function dateLocale() {
  return currentLanguage() === "nl" ? nlLocale : enUS
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    interpolation: { escapeValue: false },
    returnNull: false,
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  })

const syncHtmlLang = (lng: string) => {
  if (typeof document !== "undefined") document.documentElement.lang = lng.slice(0, 2)
}
syncHtmlLang(i18n.language ?? "en")
i18n.on("languageChanged", syncHtmlLang)

export default i18n
